import {
  AutoRefreshTokenService,
  UserActivityService,
  provideKeycloak,
  withAutoRefreshToken,
  includeBearerTokenInterceptor,
  createInterceptorCondition,
  IncludeBearerTokenCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG
} from 'keycloak-angular';
import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * Pattern of API URLs the bearer-token interceptor should attach the access
 * token to. Adjust the regex to match your protected APIs.
 */
const apiUrlCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: new RegExp(`^${escapeRegExp(environment.apiBaseUrl)}(/.*)?$`, 'i'),
  bearerPrefix: 'Bearer'
});

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Returns the providers needed to bootstrap Keycloak together with the HTTP
 * bearer-token interceptor and the auto refresh token service.
 */
export function provideAppKeycloak(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideKeycloak({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId
      },
      initOptions: {
        // Do NOT run a Keycloak SSO check on bootstrap. `check-sso` would
        // load Keycloak in an iframe to detect an existing session, but the
        // Tesvolt Keycloak instance sends `X-Frame-Options: sameorigin`,
        // which the browser refuses to load. `login-required` would force
        // every visitor (including public QR-code landing pages) through
        // the login flow.
        //
        // Instead we skip the auto-check entirely. The `authGuard` triggers
        // `keycloak.login()` on demand when a protected route is accessed,
        // and public routes load without any iframe round-trip.
        checkLoginIframe: false,
        pkceMethod: 'S256'
      },
      features: [
        withAutoRefreshToken({
          onInactivityTimeout: 'logout',
          sessionTimeout: 60_000
        })
      ],
      providers: [AutoRefreshTokenService, UserActivityService]
    }),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [apiUrlCondition]
    }
  ]);
}

export const appBearerTokenInterceptor = includeBearerTokenInterceptor;
